import React from 'react';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DoneIcon from '@mui/icons-material/Done';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import { NotificationDTO, NotificationType } from '@inversor-hub/types';
import { groupNotifications } from '@/features/notifications/utils';
import { useNavigate } from 'react-router-dom';
import { assertNever } from '@/utils/types.ts';

interface NotificationsDrawerProps {
  open: boolean;
  onClose: () => void;
  notifications: NotificationDTO[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'OPPORTUNITY_INTEREST_CREATED':
      return <PersonAddIcon />;
    case 'OPPORTUNITY_INTEREST_REJECTED':
      return <ErrorOutlineIcon />;
    case 'OPPORTUNITY_INTEREST_SELECTED':
      return <CheckCircleIcon />;
    case 'COLLABORATION_CREATED':
    case 'COLLABORATION_SIGNED_BY_PSI':
    case 'COLLABORATION_SIGNED_BY_INVESTOR':
      return <HandshakeIcon />;
    default:
      return <PersonAddIcon />;
  }
}

function getNotificationRedirectUrl(notification: NotificationDTO): string | null {
  const { type } = notification;
  switch (type) {
    case 'COLLABORATION_CREATED':
    case 'COLLABORATION_SIGNED_BY_INVESTOR':
      return '/inversiones';

    case 'COLLABORATION_SIGNED_BY_PSI':
      return '/psis';

    case 'OPPORTUNITY_INTEREST_CREATED':
    case 'OPPORTUNITY_INTEREST_REJECTED':
    case 'OPPORTUNITY_INTEREST_SELECTED':
      return `/oportunidades`;

    default:
      assertNever(type);
      return null;
  }
}

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({
  open,
  onClose,
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
}) => {
  const navigate = useNavigate();
  const groups = groupNotifications(notifications);
  const hasUnread = notifications.some((n) => !n.readAt);

  const handleNotificationClick = (notification: NotificationDTO) => {
    if (!notification.readAt) {
      onMarkAsRead(notification.id);
    }

    const redirectUrl = getNotificationRedirectUrl(notification);
    if (redirectUrl) {
      navigate(redirectUrl);
      onClose();
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 350, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Notificaciones</Typography>
            <Button
              size="small"
              onClick={onMarkAllAsRead}
              disabled={!hasUnread}
              sx={{ textTransform: 'none' }}
            >
              Marcar todo como leído
            </Button>
          </Box>
        </Box>

        <List sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, pb: 2 }}>
          {notifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                px: 2,
                textAlign: 'center',
              }}
            >
              <NotificationsOffIcon
                sx={{
                  fontSize: 48,
                  color: 'text.secondary',
                  mb: 2,
                }}
              />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                No tienes notificaciones por ahora
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={onClose}
                sx={{ textTransform: 'none' }}
              >
                Volver al panel
              </Button>
            </Box>
          ) : (
            groups.map(
              (group) =>
                group.items.length > 0 && (
                  <Box key={group.label} mb={2}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{
                        pl: 2,
                        py: 1.5,
                        fontWeight: 600,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        mb: 1,
                      }}
                    >
                      {group.label}
                    </Typography>
                    {group.items.map((notification) => {
                      const isUnread = !notification.readAt;
                      const createdAt = dayjs(notification.createdAt);
                      const notificationIcon = getNotificationIcon(notification.type);
                      const hasRedirect = getNotificationRedirectUrl(notification) !== null;

                      return (
                        <ListItemButton
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          sx={{
                            borderLeft: '4px solid',
                            borderLeftColor: isUnread ? 'primary.main' : 'transparent',
                            backgroundColor: isUnread ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                            mx: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            py: 1.5,
                            cursor: hasRedirect ? 'pointer' : 'default',
                            '&:hover': {
                              backgroundColor: isUnread
                                ? 'rgba(25, 118, 210, 0.08)'
                                : hasRedirect
                                  ? 'action.hover'
                                  : 'transparent',
                            },
                            '&:hover .quick-action': {
                              opacity: 1,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 48 }}>
                            <Box
                              sx={{
                                color: isUnread ? 'primary.main' : 'text.secondary',
                                '& svg': { fontSize: 22 },
                              }}
                            >
                              {notificationIcon}
                            </Box>
                          </ListItemIcon>

                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={isUnread ? 700 : 600}
                                  sx={{
                                    lineHeight: 1.3,
                                    flex: 1,
                                    mr: 1,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {notification.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    flexShrink: 0,
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  <Tooltip title={createdAt.format('DD/MM/YYYY HH:mm:ss')} arrow>
                                    <span>{createdAt.fromNow()}</span>
                                  </Tooltip>
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  lineHeight: 1.4,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  mt: 0.5,
                                }}
                              >
                                {notification.body}
                              </Typography>
                            }
                          />

                          {isUnread && (
                            <ListItemSecondaryAction>
                              <IconButton
                                className="quick-action"
                                edge="end"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMarkAsRead(notification.id);
                                }}
                                sx={{
                                  opacity: 0,
                                  transition: 'opacity 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                }}
                              >
                                <DoneIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItemButton>
                      );
                    })}
                  </Box>
                )
            )
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default NotificationsDrawer;
